/** Bővített hirdetés mezők — alapértelmezések és API payload. */

import { coalesceFeatureList, enrichFilledFeatures } from "@/lib/featureCoalesce";

export const CONDITIONS = [
  "Kitűnő", "Jó", "Használt", "Sérült / javított", "Restaurált", "Demo / bemutató",
];

export const DRIVE_TYPES = ["Első", "Hátsó", "Összkerék (4x4 / AWD)"];

export const EURO_CLASSES = [
  "Euro 3", "Euro 4", "Euro 5", "Euro 6", "Euro 6d", "Euro 6d-TEMP", "Ismeretlen",
];

export const ROOF_TYPES = ["Normál", "Panoráma / üvegtető", "Nyitható", "Fix üvegtető"];

export const WARRANTY_TYPES = ["", "Gyári", "Kereskedői", "Nincs"];

export const PHONE_LANG_OPTIONS = [
  { v: "hu", l: "Magyar" },
  { v: "en", l: "Angol" },
  { v: "de", l: "Német" },
  { v: "sk", l: "Szlovák" },
  { v: "ro", l: "Román" },
  { v: "sr", l: "Szerb" },
];

export const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  v: i + 1,
  l: `${i + 1}. hó`,
}));

export function isValidVin(v) {
  return /^[A-HJ-NPR-Z0-9]{17}$/i.test((v || "").trim());
}

/** Admin: listing_vin_required — üres OK, ha megadja, formátum ellenőrzés. */
export function vinMeetsListingRules(vin, { required = false } = {}) {
  const t = (vin || "").trim();
  if (!required) {
    if (!t) return true;
    if (t.length < 17) return true;
    return isValidVin(t);
  }
  return isValidVin(t);
}

/** Mi hiányzik az 1. lépésből (Tovább a képekhez) — UI tooltip. */
export function listingStep1Blockers(form, { vinRequired = false, resolveMake, resolveModel } = {}) {
  const blockers = [];
  if (!resolveMake?.(form.make)) blockers.push("márka (katalógusból)");
  else if (!resolveModel?.(form.make, form.model)) blockers.push("modell (katalógusból)");
  if (!form.engine) blockers.push("motor");
  if (!form.year) blockers.push("évjárat");
  if (!form.price) blockers.push("ár");
  if (!form.county) blockers.push("megye");
  if (!form.city?.trim()) blockers.push("város");
  const vin = (form.vin || "").trim();
  if (vinRequired && !isValidVin(vin)) blockers.push("alvázszám (17 karakter)");
  else if (vin.length === 17 && !isValidVin(vin)) blockers.push("érvénytelen alvázszám");
  return blockers;
}

export function defaultListingTitle(form) {
  const parts = [
    form.year,
    form.make,
    form.model,
    form.trim_label || form.engine,
  ].filter(Boolean);
  return parts.join(" ").trim();
}

const EXTENDED_KEYS = [
  "listing_title", "year_month", "trim_id", "trim_label", "condition", "vin",
  "tuv_valid_year", "tuv_valid_month", "displacement_cm3", "power_kw",
  "drive", "doors", "seats", "euro_class", "sale_price", "vat_reclaimable",
  "warranty_type", "warranty_until", "warranty_km", "trade_in_possible", "immediate_available",
  "finance_available", "finance_down_payment", "finance_monthly", "finance_months", "lease_available",
  "listing_phone", "seller_phone_2", "phone_languages", "show_email_public", "public_email",
  "viewing_address", "stock_id", "color_metallic", "roof_type", "interior_color", "interior_color_2",
  "trunk_liters", "curb_weight_kg", "gross_weight_kg",
];

export function extendedFieldsFromCar(car) {
  const base = {};
  for (const k of EXTENDED_KEYS) {
    if (car && car[k] !== undefined && car[k] !== null) base[k] = car[k];
  }
  if (!base.phone_languages) base.phone_languages = [];
  if (base.immediate_available === undefined) base.immediate_available = true;
  return base;
}

export function emptyExtendedFields() {
  return {
    listing_title: "",
    year_month: null,
    trim_id: "",
    trim_label: "",
    condition: "",
    vin: "",
    tuv_valid_year: null,
    tuv_valid_month: null,
    displacement_cm3: null,
    power_kw: null,
    drive: "",
    doors: null,
    seats: null,
    euro_class: "",
    sale_price: null,
    vat_reclaimable: false,
    warranty_type: "",
    warranty_until: "",
    warranty_km: null,
    trade_in_possible: false,
    immediate_available: true,
    finance_available: false,
    finance_down_payment: null,
    finance_monthly: null,
    finance_months: null,
    lease_available: false,
    listing_phone: "",
    seller_phone_2: "",
    phone_languages: [],
    show_email_public: false,
    public_email: "",
    viewing_address: "",
    stock_id: "",
    color_metallic: false,
    roof_type: "",
    interior_color: "",
    interior_color_2: "",
    trunk_liters: null,
    curb_weight_kg: null,
    gross_weight_kg: null,
  };
}

const TRIM_SPEC_KEYS = new Set([
  "fuel", "transmission", "engine", "drive", "doors", "seats",
  "displacement_cm3", "power_kw", "horsepower", "acceleration",
  "body_type", "euro_class", "trunk_liters", "trim_id", "trim_label",
  "make", "model", "year", "vin",
]);

export function applyTrimSpecs(form, specs) {
  if (!specs || !Object.keys(specs).length) return form;
  const patch = {};
  for (const [k, v] of Object.entries(specs)) {
    if (v !== undefined && v !== null && v !== "" && TRIM_SPEC_KEYS.has(k)) {
      patch[k] = v;
    }
  }
  const next = { ...form, ...patch };
  if (patch.trim_label) next.trim_label = patch.trim_label;
  if (patch.trim_id) next.trim_id = patch.trim_id;
  if (!next.listing_title || next.listing_title === defaultListingTitle(form)) {
    next.listing_title = defaultListingTitle(next);
  }
  return next;
}

const VIN_FIELD_LABELS = {
  make: "márka",
  model: "modell",
  year: "évjárat",
  fuel: "üzemanyag",
  drive: "hajtás",
  displacement_cm3: "hengerűrtartalom",
  engine: "motor",
  transmission: "váltó",
  body_type: "karosszéria",
  trim_label: "kivitel",
  horsepower: "LE",
  acceleration: "0–100",
};

/** VIN decode API `filled` mezői → űrlap */
export function applyVinDecode(form, decodeResponse) {
  const filled = decodeResponse?.filled || {};
  if (Object.keys(filled).length > 1) {
    return applyTrimSpecs(form, filled);
  }
  // régi API válasz (mezők a gyökéren)
  return applyTrimSpecs(form, decodeResponse);
}

export function describeVinFilled(filled = {}) {
  return Object.keys(filled)
    .filter((k) => k !== "vin" && filled[k] != null && filled[k] !== "")
    .map((k) => VIN_FIELD_LABELS[k] || k);
}

const IMPORT_FIELD_LABELS = {
  ...VIN_FIELD_LABELS,
  vin: "alvázszám",
  county: "megye",
  city: "város",
  price: "ár",
  mileage: "kilométer",
  description: "leírás",
  color: "szín",
  condition: "állapot",
};

const IMPORT_EXTRA_KEYS = [
  "price", "mileage", "description", "color", "condition",
  "year_month", "roof_type", "color_metallic", "trade_in_possible",
  "county", "city",
];

export function applyImportFilled(form, result) {
  let filled = result?.filled || {};
  if (!Object.keys(filled).length) return form;
  filled = enrichFilledFeatures(filled);
  const next = applyTrimSpecs(form, filled);
  for (const k of IMPORT_EXTRA_KEYS) {
    if (filled[k] != null && filled[k] !== "") next[k] = filled[k];
  }
  if (Array.isArray(filled.features) && filled.features.length) {
    next.features = coalesceFeatureList([...(next.features || []), ...filled.features]);
  }
  if (!next.listing_title?.trim()) {
    next.listing_title = defaultListingTitle(next);
  }
  return next;
}

export function describeImportFilled(filled = {}) {
  const labels = Object.keys(filled)
    .filter((k) => k !== "features" && filled[k] != null && filled[k] !== "")
    .map((k) => IMPORT_FIELD_LABELS[k] || k);
  const featCount = Array.isArray(filled.features) ? filled.features.length : 0;
  if (featCount) labels.push(`felszereltség (${featCount})`);
  return labels;
}

export function pickExtendedPayload(form) {
  const out = {};
  for (const k of EXTENDED_KEYS) {
    const v = form[k];
    if (v === "" || v === null || v === undefined) continue;
    if (k === "phone_languages" && Array.isArray(v) && !v.length) continue;
    out[k] = v;
  }
  if (form.horsepower != null && form.horsepower !== "") out.horsepower = Number(form.horsepower);
  if (form.acceleration != null && form.acceleration !== "") out.acceleration = Number(form.acceleration);
  return out;
}
